import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import SearchBar from '../../../../ui/canon/SearchBar';
import { categoriesData } from '../Home/categoriesData';
import PartnerMiniGrid from '../Succession/PartnerMiniGrid';
import SellerHeatMap from '../Succession/SellerHeatMap';
import PartialSalesMiniList from '../Succession/PartialSales/PartialSalesMiniList';
import RolodexMiniCard from '../Succession/RolodexMiniCard';
import { SnackbarProvider } from 'notistack';
import SuccessionThemeProvider from '../Succession/SuccessionThemeProvider';
import SuccessionDrawerWithListener from '../../containers/Succession/SuccessionDrawerWithListener';
import Card from '../../../../ui/canon/Card';
import { CategoryTemplate, ToolsGridTemplate } from '../../../../ui/layouts';
import { gql, useQuery } from '@apollo/client';
import ListCard from '../Succession/Search/ListCard';
import Button from '../../../../ui/canon/Button';
import Tabs from '../../../../ui/canon/Tabs.jsx';
import ProfileCompletionCard from '../Succession/ProfileCompletionCard';
import ValuationSnapshot from '../Succession/ValuationSnapshot';
import MultiLineValuationSnapshot from '../Succession/MultiLineValuationSnapshot';
import { TrendingUp } from 'lucide-react';

const GrowthExplorerContent = () => {
  const items = [
    (<PartnerMiniGrid />),
    (<RolodexMiniCard />),
    (<SellerHeatMap />),
  ];
  return (
    <div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
          {items.map((item, idx) => (
            <div key={idx} className="h-full">
              {item}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const GrowthCategoryPage = (props) => {
  const intl = useIntl();
  const name = (props?.current_user && props.current_user.name) || (typeof document !== 'undefined' && document.store?.getState?.()?.user?.name) || 'there';
  const features = props?.features || (props?.company && (props.company.feature_types || props.company.features)) || [];
  const multiLineValuationEnabled = Array.isArray(features) && features.includes('multi_line_valuation');
  const [searchQuery, setSearchQuery] = useState('');
  const [oppsTab, setOppsTab] = useState('recent'); // 'recent' | 'rolodex'
  // Derive tools for the growth category (used in sidebar tools grid)
  const cat = (categoriesData || []).find((c) => c.id === 'opportunity');
  const tools = (cat && cat.tools) || [];

  // Query: most recent opportunities (leverages same userSearch data shape as SuccessionListSearch)
  const RECENT_USERS_QUERY = gql`
    query RecentUsers($page: Int, $perPage: Int, $locationWithin: String) {
      company { id country business_type }
      userSearch(page: $page, perPage: $perPage, locationWithin: $locationWithin) {
        total_count
        items {
          id
          user_name
          fb_user_name
          allowed_name
              transition_goals
          headline
          city
          province
          profile_picture
          annual_revenue
          assets_under_management
          number_of_clients
          years_practicing
          visible_profile_features_for_user
          is_student
          amp_trainee_status
          has_partial_sales
          note_count
        }
      }
    }
  `;

  const { data: recentData, loading: recentLoading, error: recentError, refetch } = useQuery(RECENT_USERS_QUERY, {
    variables: { page: 1, perPage: 10, locationWithin: "5000" },
    fetchPolicy: 'cache-first',
  });

  // Query: rolodex entries
  const GET_ROLODEX = gql`
    query GetRolodexForGrowth {
      getRolodex {
        id
        user_name
        fb_user_name
        allowed_name
            transition_goals
        headline
        city
        province
        profile_picture
        annual_revenue
        assets_under_management
        number_of_clients
        years_practicing
        visible_profile_features_for_user
        is_student
        amp_trainee_status
        has_partial_sales
        note_count
        created_at
      }
      company { id country business_type }
    }
  `;

  const { data: rolodexData, loading: rolodexLoading, error: rolodexError, refetch: refetchRolodex } = useQuery(GET_ROLODEX, { fetchPolicy: 'cache-first' });

  // Listen for shortlist/invite events to refetch data
  React.useEffect(() => {
    const handleDataUpdate = (event) => {
      console.log('GrowthCategoryPage: Data update event received, refetching queries');
      // Refetch both queries to ensure freshness
      if (refetch) refetch();
      if (refetchRolodex) refetchRolodex();
    };

    window.addEventListener('advisor-shortlisted-navigate', handleDataUpdate);
    window.addEventListener('advisor-nda-sent-navigate', handleDataUpdate);
    window.addEventListener('advisor-invited-navigate', handleDataUpdate);

    return () => {
      window.removeEventListener('advisor-shortlisted-navigate', handleDataUpdate);
      window.removeEventListener('advisor-nda-sent-navigate', handleDataUpdate);
      window.removeEventListener('advisor-invited-navigate', handleDataUpdate);
    };
  }, [refetch, refetchRolodex]);

  const recentUsers = recentData?.userSearch?.items || [];
  const rolodexUsers = useMemo(() => {
    const all = rolodexData?.getRolodex || [];
    return [...all].sort((a, b) => (new Date(b?.created_at || 0)) - (new Date(a?.created_at || 0))).slice(0, 10);
  }, [rolodexData]);
  const rolodexCount = rolodexData?.getRolodex?.length || 0;
  const tabs = [
    {
      id: 'recent',
      label: intl.formatMessage({ id: 'succession.growth.tabs.most_recent' }),
      description: intl.formatMessage({ id: 'succession.growth.tabs.most_recent_description' }),
    },
    {
      id: 'rolodex',
      label: intl.formatMessage({ id: 'succession.growth.tabs.your_rolodex' }, { count: rolodexCount }),
      description: intl.formatMessage({ id: 'succession.growth.tabs.your_rolodex_description' }),
    },
  ];

  return (
    <>
      <CategoryTemplate
        title={intl.formatMessage({ id: 'succession.growth.explore_title' })}
        variant="wide"
        hero={{
          title: intl.formatMessage({ id: 'succession.growth.hero_title' }),
          blurb: intl.formatMessage({ id: 'succession.growth.hero_blurb' }),
          ctaHref: '/succession/education?category=Value%20Acceleration',
          ctaLabel: intl.formatMessage({ id: 'succession.growth.hero_cta' }),
          variant: 'brand',
          icon: TrendingUp,
        }}
        sidebar={(
          <>
            <ProfileCompletionCard elevateOnHover={false} className="mb-4" />
            {multiLineValuationEnabled ? (
              <MultiLineValuationSnapshot showHeaderIcon={false} compact className="mb-4" />
            ) : (
              <ValuationSnapshot showHeaderIcon={false} compact className="mb-4" />
            )}
            <ToolsGridTemplate title={intl.formatMessage({ id: 'succession.growth.popular_tools' })} tools={tools} />
          </>
        )}
      >
        {/* Search bar below banner */}
        <div className="mb-4 w-full">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSubmit={(q) => { window.location.href = `/succession/search?prompt=${encodeURIComponent(q)}`; }}
            variant="outline"
            size="md"
          />
        </div>
        {/* Opportunities you might like */}
        <div className="mb-4">
          <h3 className="font-semibold text-lg text-succession-text-primary leading-tight mb-2" style={{ margin: 0, marginBottom: '0.5rem', padding: 0 }}>{intl.formatMessage({ id: 'succession.growth.opportunities_heading' })}</h3>
          <Tabs tabs={tabs} value={oppsTab} onChange={setOppsTab} showDescription />
          <Card variant="borderless">
            <div className="p-2">
              {oppsTab === 'recent' ? (
                recentLoading ? (
                  <div className="p-4 text-succession-text-secondary text-sm">{intl.formatMessage({ id: 'succession.growth.loading' })}</div>
                ) : recentError ? (
                  <div className="p-4 text-succession-error text-sm">{intl.formatMessage({ id: 'succession.growth.error_recent' })}</div>
                ) : (
                  <div className="space-y-3">
                    {recentUsers.slice(0, 10).map((u) => (
                      <ListCard key={u.id} user={u} currentCompany={recentData?.company} />
                    ))}
                    <div className="pt-2 flex justify-center">
                      <Button as="a" href="/succession/search" className="rounded-full text-sm">
                        {intl.formatMessage({ id: 'succession.growth.discover_more' })}
                      </Button>
                    </div>
                  </div>
                )
              ) : (
                rolodexLoading ? (
                  <div className="p-4 text-succession-text-secondary text-sm">{intl.formatMessage({ id: 'succession.growth.loading' })}</div>
                ) : rolodexError ? (
                  <div className="p-4 text-succession-error text-sm">{intl.formatMessage({ id: 'succession.growth.error_rolodex' })}</div>
                ) : (
                  <div className="space-y-3">
                    {rolodexUsers.map((u) => (
                      <ListCard key={u.id} user={u} currentCompany={rolodexData?.company} />
                    ))}
                    <div className="pt-2 flex justify-center">
                      <Button as="a" href="/succession/rolodex" className="rounded-full text-sm">
                        {intl.formatMessage({ id: 'succession.growth.see_rolodex' })}
                      </Button>
                    </div>
                  </div>
                )
              )}
            </div>
          </Card>
        </div>
        <Card>
        </Card>
      </CategoryTemplate>

      <SnackbarProvider maxSnack={3}>
        <SuccessionThemeProvider company={props.company} user={props.current_user}>
          <SuccessionDrawerWithListener />
        </SuccessionThemeProvider>
      </SnackbarProvider>
    </>
  );
};

export default GrowthCategoryPage;





